import { Select, MenuItem, SelectChangeEvent, Box, Typography, FormControl } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { updateCurrency, fetchRecommendedCurrency } from '~/actions';
import { CURRENCY, EURO_SIGN, USD_SIGN, ZAR_SIGN, GBP_SIGN, PLN_SIGN, SELECTED_CURRENCY_STORAGE_KEY } from '~/literals';
import { AppState, Currency, RootState } from '~/types';
import { useLocation } from 'react-router-dom';

interface CurrencyOption {
  value: Currency;
  label: string;
  flag: string;
}

const currencyOptions: CurrencyOption[] = [
  { value: CURRENCY.EUR, label: `${CURRENCY.EUR}(${EURO_SIGN})`, flag: '//flagcdn.com/w20/eu.png' },
  { value: CURRENCY.GBP, label: `${CURRENCY.GBP}(${GBP_SIGN})`, flag: '//flagcdn.com/w20/gb.png' },
  { value: CURRENCY.ZAR, label: `${CURRENCY.ZAR}(${ZAR_SIGN})`, flag: '//flagcdn.com/w20/za.png' },
  { value: CURRENCY.USD, label: `${CURRENCY.USD}(${USD_SIGN})`, flag: '//flagcdn.com/w20/us.png' },
  { value: CURRENCY.PLN, label: `${CURRENCY.PLN}(${PLN_SIGN})`, flag: '//flagcdn.com/w20/pl.png' },
];

const setTpwlCurrencyCookie = (currency: Currency) => {
  const rootDomain = window.location.hostname.replace(/^www\./, '');
  const domainAttribute = rootDomain === 'localhost' ? '' : ` domain=.${rootDomain};`;

  document.cookie = `tpwl_currency=${currency}; path=/;${domainAttribute} max-age=31536000`;
};

const CurrencySelector = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const app = useSelector<RootState, AppState>(({ app }) => app);
  const { currency, disableCurrencySelector, loadingRecommendedCurrency } = app;
  const requestedRecommendedCurrencyRef = useRef(false);
  const recommendationRequestStartedRef = useRef(false);
  const storedRecommendedCurrencyCookieRef = useRef(false);

  const isPreBookingPage = location.pathname.includes('/flights/pre-booking/');
  // const isItineraryPage = location.pathname.startsWith('/itinerary');

  useEffect(() => {
    // Check if currency exists in localStorage
    const storedCurrency = localStorage.getItem(SELECTED_CURRENCY_STORAGE_KEY) as Currency | null;

    if (storedCurrency) {
      // Use stored currency if it exists
      if (storedCurrency !== currency) {
        dispatch(updateCurrency(storedCurrency));
      }
    } else {
      // Fetch recommended currency from server
      requestedRecommendedCurrencyRef.current = true;
      dispatch(fetchRecommendedCurrency());
    }
  }, []);

  useEffect(() => {
    if (requestedRecommendedCurrencyRef.current && loadingRecommendedCurrency) {
      recommendationRequestStartedRef.current = true;
    }

    if (
      requestedRecommendedCurrencyRef.current &&
      recommendationRequestStartedRef.current &&
      !storedRecommendedCurrencyCookieRef.current &&
      !loadingRecommendedCurrency &&
      currency
    ) {
      setTpwlCurrencyCookie(currency);
      storedRecommendedCurrencyCookieRef.current = true;
    }
  }, [currency, loadingRecommendedCurrency]);

  const handleChange = (event: SelectChangeEvent<Currency>) => {
    const newCurrency = event.target.value as Currency;
    dispatch(updateCurrency(newCurrency));

    // Update the tpwl_currency cookie for both www and non-www domains, then reload the page
    setTpwlCurrencyCookie(newCurrency);
    window.location.reload();
  };

  // Don't show on pre-booking pages, but allow on homepage, flights and itinerary
  if (isPreBookingPage) {
    return null;
  }

  return (
    <Box sx={{ display: 'inline' }}>
      <FormControl variant="standard" sx={{ m: 0, minWidth: 120 }} size='small'>
        <Select
          className='currencySelector'
          value={currency}
          onChange={handleChange}
          displayEmpty
          disabled={disableCurrencySelector || isPreBookingPage}
          renderValue={(value) => {
            if (!value) {
              return <Typography>Currency</Typography>;
            }
            const selected = currencyOptions.find(option => option.value === value);
            return (
              <Box display="flex" alignItems="center">
                <img src={selected?.flag} alt={`${selected?.label} flag`} width={20} style={{ marginRight: 5 }} />
                <Typography variant='body2'>{selected?.label}</Typography>
              </Box>
            );
          }}
        >
          {currencyOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Box display="flex" alignItems="center">
                <img src={option.flag} alt={`${option.label} flag`} width={20} style={{ marginRight: 5 }} />
                <Typography variant='body2'>{option.label}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CurrencySelector;
